import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "@/components/Avatar";
import type { Notification } from "@/messaging/types";
import { CloseIcon, MegaphoneIcon } from "@/messaging/chat/icons";
import { announcementId, timeAgo, unusedQuickReactions } from "./shared";

/** How long a toast sits on screen before retiring itself. Long enough to
 *  read a headline and react without reaching for it; the hover pause
 *  covers anyone who wants longer. */
const DWELL_MS = 10_000;
/** Below the z-[1350] modals (so clicking a toast open doesn't leave it
 *  floating over its own detail view) and above the chat drawer's
 *  z-[1100], which would otherwise bury it. */
const Z = "z-[1300]";

/** Arrival popups for broadcast-shaped notifications.
 *
 *  Without these an announcement lands silently: the bell badge ticks up
 *  in the corner and nothing else happens, which is why broadcasts were
 *  routinely missed. Non-blocking by design — the alternative, a modal
 *  takeover, interrupts whatever the recipient is mid-way through.
 *
 *  Stacked newest-first and capped by the provider; the whole stack is
 *  portaled so no ancestor's overflow or stacking context can clip it. */
export function AnnouncementToasts({
  toasts,
  onOpen,
  onDismiss,
  onReact,
}: {
  toasts: Notification[];
  onOpen: (n: Notification) => void;
  onDismiss: (id: string) => void;
  onReact: (announcementId: string, emoji: string) => void;
}) {
  if (typeof document === "undefined" || toasts.length === 0) return null;

  return createPortal(
    <div
      className={`pointer-events-none fixed bottom-4 right-4 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2 ${Z}`}
      role="region"
      aria-label="New announcements"
    >
      {toasts.map((n) => (
        <ToastCard
          key={n.id}
          n={n}
          onOpen={() => onOpen(n)}
          onDismiss={() => onDismiss(n.id)}
          onReact={(emoji) => {
            const aid = announcementId(n);
            if (aid) onReact(aid, emoji);
          }}
        />
      ))}
    </div>,
    document.body,
  );
}

function ToastCard({
  n,
  onOpen,
  onDismiss,
  onReact,
}: {
  n: Notification;
  onOpen: () => void;
  onDismiss: () => void;
  onReact: (emoji: string) => void;
}) {
  // Paused while the pointer is over the card or focus is inside it —
  // auto-dismissing something the reader is actively engaging with is the
  // one thing a toast must never do.
  const [paused, setPaused] = useState(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Time still owed when the timer is next started. The CSS bar resumes
  // from where it froze, so the timer has to carry its remainder across a
  // pause too — restarting a full DWELL_MS would leave the card sitting
  // there long after the bar had emptied.
  const remainingRef = useRef(DWELL_MS);

  useEffect(() => {
    if (paused) return;
    const startedAt = Date.now();
    const t = window.setTimeout(() => onDismissRef.current(), remainingRef.current);
    return () => {
      window.clearTimeout(t);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [paused]);

  const sender = n.payload as {
    sender_name?: string;
    sender_email?: string;
    sender_avatar_url?: string | null;
  };
  const quick = unusedQuickReactions(n).slice(0, 6);

  return (
    <div
      className="pointer-events-auto overflow-hidden rounded-2xl border border-accent-500/25 bg-ink-900/95 shadow-2xl shadow-black/50 backdrop-blur-md animate-[toastIn_.22s_cubic-bezier(.2,.8,.3,1)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent-300">
          <MegaphoneIcon size={13} /> Announcement
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="icon-btn h-6 w-6"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <CloseIcon size={13} />
        </button>
      </div>

      <button
        type="button"
        onClick={onOpen}
        className="block w-full px-3.5 py-2.5 text-left transition hover:bg-white/[0.03]"
      >
        <div className="text-[13px] font-semibold leading-snug text-slate-100">{n.title}</div>
        {n.body && (
          <div className="mt-1 line-clamp-2 text-[12px] leading-snug text-slate-400">{n.body}</div>
        )}
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
          {sender.sender_name && (
            <>
              <Avatar
                url={sender.sender_avatar_url}
                name={sender.sender_name}
                email={sender.sender_email}
                size="xs"
                className="!h-4 !w-4"
              />
              <span className="truncate">{sender.sender_name}</span>
              <span>·</span>
            </>
          )}
          <span>{timeAgo(n.created_at)}</span>
        </div>
      </button>

      <div className="flex flex-wrap items-center gap-1 px-3.5 pb-2.5">
        {n.reactions.map((r) => (
          <button
            key={r.emoji}
            type="button"
            onClick={() => onReact(r.emoji)}
            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition ${
              r.me
                ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20"
            }`}
          >
            <span className="text-[12px] leading-none">{r.emoji}</span>
            <span className="tabular-nums">{r.count}</span>
          </button>
        ))}
        {quick.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => onReact(e)}
            className="h-6 w-6 rounded-full text-[15px] leading-none opacity-60 transition hover:bg-white/10 hover:opacity-100"
            title={`React ${e}`}
          >
            {e}
          </button>
        ))}
      </div>

      {/* Dwell indicator. Animation-based rather than stateful so it costs
          no renders; freezing it on hover mirrors the paused timer. */}
      <div className="h-0.5 w-full bg-white/[0.04]">
        <div
          className="h-full bg-accent-500/60"
          style={{
            animation: `toastDwell ${DWELL_MS}ms linear forwards`,
            animationPlayState: paused ? "paused" : "running",
          }}
        />
      </div>
    </div>
  );
}
